import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Filter, ArrowRight, CheckCircle, Mail, Phone, ChevronDown, Settings, Droplets, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import DealerLogos from '../components/DealerLogos';

const FiltrationLandingPage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "When should I choose Brush cleaning vs Vacuum cleaning?",
      answer: "Brush cleaning (BIF semi-auto / RAB full-auto) excels with moderate solids loads, organic debris, and algae—ideal for well or canal water where mechanical simplicity is preferred. Vacuum cleaning (MVC semi-auto / AVC full-auto) handles heavy or variable solids loads, fine mesh screens, and critical applications requiring maximum uptime with minimal water loss during backflush cycles. Vacuum systems complete cleaning in 10-20 seconds and work well with high-frequency cleaning schedules."
    },
    {
      question: "What's the difference between Manual (semi-automatic) and Automatic cleaning?",
      answer: "Semi-automatic systems (BIF brush, MVC vacuum) require operator initiation—cleaning is started by turning a hand crank or opening a valve, making them ideal for accessible sites where operators can perform periodic maintenance. Fully automatic systems (RAB brush, AVC vacuum) use sensors (differential pressure or timers) and motors to initiate cleaning cycles without human intervention, perfect for remote sites, continuous operations, or applications requiring unattended operation."
    },
    {
      question: "How do I size my intake suction screen?",
      answer: "Intake screen sizing depends on pump flow rate, water velocity requirements (especially for fish-safe applications), debris load, and whether you need self-cleaning or passive operation. Self-cleaning screens (SCS2) use pump discharge water for automatic cleaning and work well with variable debris. Passive screens (PSF) maintain <0.2 ft/sec approach velocity for fish safety and require adequate screen area for your flow rate. Contact our filtration specialist team for proper sizing based on your specific application."
    },
    {
      question: "Should I use mesh or perforated plate screens?",
      answer: "Mesh screens (20-100 mesh) provide precise filtration for drip and micro-irrigation systems where small particle removal is critical. Perforated plate offers high strength and durability for coarser filtration needs like intake protection and center pivot systems. We also offer wedgewire for fish-safe applications. The choice depends on your emitter orifice size, solids type, and maintenance preferences. Custom mesh sizes and perforation patterns are available—we're not limited to the standard options listed."
    },
    {
      question: "What are typical maintenance intervals for inline filters?",
      answer: "Maintenance intervals vary widely based on water quality, debris load, and screen mesh size. Semi-automatic systems (BIF brush, MVC vacuum) may need operator-initiated cleaning daily to weekly depending on conditions. Fully automatic systems (RAB brush, AVC vacuum) can handle higher frequencies and adapt to changing conditions via differential pressure sensors. Heavier debris loads or finer mesh requires more frequent cleaning. Proper sizing and cleaning technology selection minimize maintenance demands—our filtration specialist team can help estimate intervals for your specific water source."
    },
    {
      question: "Can I retrofit cleaning technology onto an existing filter body?",
      answer: "Yes! Both brush (BIF semi-auto / RAB full-auto) and vacuum (MVC semi-auto / AVC full-auto) cleaning modules are designed as retrofit kits that mount directly to BIG Series filter bodies. No repiping or welding required—the modules attach to the top head, making it easy to upgrade from manual disassembly cleaning to semi-automatic or fully automatic operation as your needs evolve."
    }
  ];

  return (
    <>
      <SEOHead
        title="Filtration Systems for Agriculture, Municipal, Industrial & Hydropower | CTC Waterworks"
        description="Engineered stainless-steel filtration systems for irrigation, municipal, industrial, and hydropower applications. Protect pumps, membranes, and intakes with automated self-cleaning technology."
        canonical="https://www.ctcwaterworks.com/filtration"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-ctc-blue to-blue-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Main Content - Left 2/3 */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h1 className="text-5xl font-bold mb-4">
                    Engineered Filtration Systems for Any Water Source
                  </h1>
                  <p className="text-2xl text-blue-100 mb-8 font-light leading-relaxed">
                    From irrigation networks and municipal water systems to industrial process streams and hydropower intakes — CTC filtration systems protect pumps, membranes, and entire water networks with precision-engineered stainless-steel construction and automated cleaning technology.
                  </p>

                  <div className="mb-8">
                    <p className="text-lg text-blue-100 leading-relaxed">
                      Our filtration solutions are built for performance and longevity across any environment where solids removal matters. Choose your application or industry below to explore options for inline pressure filters, suction screens, and water box assemblies.
                    </p>
                  </div>

                  {/* By Application/Industry Quick Links */}
                  <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="font-bold text-white mb-2">By Application</h3>
                        <ul className="text-sm text-blue-100 space-y-1">
                          <li>• Intake protection</li>
                          <li>• Pre-filtration</li>
                          <li>• Process water polishing</li>
                          <li>• Canal/river intake</li>
                          <li>• Pump suction protection</li>
                          <li>• Dairy lagoons / manure ponds</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-2">By Industry</h3>
                        <div className="space-y-2">
                          <Link to="/industries/agriculture" className="block text-sm text-blue-100 hover:text-white transition-colors">→ Agriculture</Link>
                          <Link to="/industries/municipal" className="block text-sm text-blue-100 hover:text-white transition-colors">→ Municipal</Link>
                          <Link to="/industries/industrial" className="block text-sm text-blue-100 hover:text-white transition-colors">→ Industrial</Link>
                          <Link to="/industries/hydropower" className="block text-sm text-blue-100 hover:text-white transition-colors">→ Hydropower</Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Acronym Legend */}
                  <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 mb-8">
                    <h3 className="font-bold text-lg mb-3 text-white">Quick Reference</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-100">
                      <div><span className="font-semibold text-white">BIG</span> = Blue Inline Gasketed (pressure filters)</div>
                      <div><span className="font-semibold text-white">TIG</span> = True Inline Gasketed (pressure filters)</div>
                      <div><span className="font-semibold text-white">BIF</span> = Manual Brush, <span className="font-semibold text-white">RAB</span> = Automatic Brush</div>
                      <div><span className="font-semibold text-white">MVC</span> = Manual Vacuum, <span className="font-semibold text-white">AVC</span> = Automatic Vacuum</div>
                      <div><span className="font-semibold text-white">SCS2</span> = Self-Cleaning Suction Screen</div>
                      <div><span className="font-semibold text-white">PSF</span> = Passive Screen for Fish</div>
                      <div><span className="font-semibold text-white">WB</span> = Water Box (coarse intake)</div>
                      <div><span className="font-semibold text-white">BWB</span> = Basket with Bag (fine filtration)</div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Right Rail CTAs - Desktop */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-xl p-6 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Get Expert Help</h3>

                  <Link
                    to="/contact?topic=filtration"
                    className="block w-full bg-ctc-blue text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center shadow-lg"
                  >
                    <Mail className="inline-block mr-2 mb-1" size={20} />
                    Talk to a Filtration Specialist
                  </Link>

                  <Link
                    to="/filtration/size-my-filter"
                    className="block w-full bg-green-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors text-center shadow-lg"
                  >
                    <Settings className="inline-block mr-2 mb-1" size={20} />
                    Size My Filter
                  </Link>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-3 text-gray-700 mb-3">
                      <Phone size={20} />
                      <span className="text-sm">(509) 901-7476</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Mail size={20} />
                      <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-ctc-blue underline">
                        Andre@ctcwaterworks.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product Showcase Section */}
        <section className="py-12 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="/BIG Catalog Pic.jpeg"
                  alt="BIG Series inline pressure filter - flagship stainless steel filtration system"
                  className="w-full h-auto"
                />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Industry-Leading Filtration Technology</h3>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  CTC Waterworks BIG Series represents over four decades of engineering excellence in water filtration. Precision-machined stainless steel construction, gasket-sealed reliability, and modular cleaning systems deliver consistent performance across the most demanding applications.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircle className="mr-3 text-ctc-blue mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h4 className="font-semibold text-gray-900">Proven Performance</h4>
                      <p className="text-sm text-gray-700">Trusted by agricultural, municipal, and industrial operations nationwide</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-3 text-green-600 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h4 className="font-semibold text-gray-900">Modular Design</h4>
                      <p className="text-sm text-gray-700">Start manual, upgrade to automatic—retrofit cleaning systems as needs evolve</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-3 text-orange-600 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h4 className="font-semibold text-gray-900">Stainless Steel Construction</h4>
                      <p className="text-sm text-gray-700">304/316 stainless steel for decades of reliable service</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Inline Pressure Filters Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <Link to="/filtration/inline">
              <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center hover:text-ctc-blue transition-colors cursor-pointer">Inline Pressure Filters (BIG & TIG)</h2>
            </Link>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Designed for any pressurized water system — agricultural, municipal, industrial, or hydropower — delivering durable stainless-steel performance with manual or automated cleaning.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* BIG Series Card */}
              <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-ctc-blue rounded-lg flex items-center justify-center mr-4">
                    <Filter className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">BIG Series</h3>
                    <p className="text-sm text-gray-600">Blue Inline Gasketed</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Versatile inline pressure filter supporting multiple cleaning technologies across all industries. Protect pumps, valves, heat exchangers, and emitters with reliable stainless-steel construction.
                </p>

                <div className="space-y-3 mb-6">
                  <Link
                    to="/filtration/inline/big"
                    className="flex items-center justify-between bg-white border-2 border-ctc-blue text-ctc-blue px-4 py-3 rounded-lg font-semibold hover:bg-ctc-blue hover:text-white transition-colors"
                  >
                    <span>BIG Series Overview</span>
                    <ArrowRight size={20} />
                  </Link>
                </div>

                <div className="bg-white rounded-lg p-4 space-y-2">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Cleaning Technologies:</p>
                  <Link
                    to="/filtration/inline/big/brush-cleaning"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue group"
                  >
                    <CheckCircle className="mr-2 text-green-600 group-hover:text-ctc-blue" size={16} />
                    <span>Brush Cleaning (BIF Manual + RAB Automatic)</span>
                  </Link>
                  <Link
                    to="/filtration/inline/big/vacuum-cleaning"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue group"
                  >
                    <CheckCircle className="mr-2 text-orange-600 group-hover:text-ctc-blue" size={16} />
                    <span>Vacuum Cleaning (MVC Manual + AVC Automatic)</span>
                  </Link>
                </div>
              </div>

              {/* BIG-S Series Card */}
              <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-300 rounded-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-gray-600 rounded-lg flex items-center justify-center mr-4">
                    <Filter className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">BIG-S Series</h3>
                    <p className="text-sm text-gray-600">Stainless Inline</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Cost-optimized stainless steel inline filter with modular universal ports. Precision filtration performance in a streamlined package ideal for budget-conscious projects.
                </p>

                <Link
                  to="/filtration/inline/big-s"
                  className="flex items-center justify-between bg-white border-2 border-gray-600 text-gray-600 px-4 py-3 rounded-lg font-semibold hover:bg-gray-600 hover:text-white transition-colors"
                >
                  <span>BIG-S Series Details</span>
                  <ArrowRight size={20} />
                </Link>

                <div className="mt-6 bg-green-50 border-l-4 border-green-600 p-4 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Value Stainless:</span> Premium 304 stainless construction at an accessible price point.
                  </p>
                </div>
              </div>

              {/* GNG Series Card */}
              <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                    <Filter className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">GNG Series</h3>
                    <p className="text-sm text-gray-600">Non-Gasketed Economy</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Budget-friendly inline filtration with ring-lock quick-release head and painted mild-steel body. Economical upfront cost with fast maintenance access.
                </p>

                <Link
                  to="/filtration/inline/gng"
                  className="flex items-center justify-between bg-white border-2 border-green-600 text-green-600 px-4 py-3 rounded-lg font-semibold hover:bg-green-600 hover:text-white transition-colors"
                >
                  <span>GNG Series Details</span>
                  <ArrowRight size={20} />
                </Link>

                <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Quick-Release Head:</span> Ring-lock design for tool-free screen removal and cleaning.
                  </p>
                </div>
              </div>

              {/* TIG Series Card */}
              <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 rounded-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-orange-600 rounded-lg flex items-center justify-center mr-4">
                    <Filter className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">TIG Series</h3>
                    <p className="text-sm text-gray-600">True Inline Gasketed</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Compact inline design with quick-access end caps for manual cleaning. Ideal for space-constrained installations, municipal pre-treatment, industrial process water, and applications where true-inline flow path is required.
                </p>

                <Link
                  to="/filtration/inline/tig"
                  className="flex items-center justify-between bg-white border-2 border-orange-600 text-orange-600 px-4 py-3 rounded-lg font-semibold hover:bg-orange-600 hover:text-white transition-colors"
                >
                  <span>TIG Series Details</span>
                  <ArrowRight size={20} />
                </Link>

                <div className="mt-6 bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Compact Design:</span> Minimal footprint with full-flow, low head-loss configuration for tight spaces.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Suction Screens Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <Link to="/filtration/suction-screens">
              <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center hover:text-ctc-blue transition-colors cursor-pointer">Suction Screens</h2>
            </Link>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Protect pumps and filtration equipment from large debris at the source. Available with configurations meeting NOAA/WDFW fish-screen approach-velocity and open-area criteria — approved for surface-water and dam intake use.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Standard Suction Screens Card */}
              <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-300 rounded-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-gray-600 rounded-lg flex items-center justify-center mr-4">
                    <Filter className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Standard Suction Screens</h3>
                    <p className="text-sm text-gray-600">Manual Cleaning</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Traditional pump intake screens with removable basket design for manual cleaning. Cost-effective protection for ponds, wells, and accessible water sources.
                </p>

                <Link
                  to="/filtration/suction-screens/standard"
                  className="flex items-center justify-between bg-white border-2 border-gray-600 text-gray-600 px-4 py-3 rounded-lg font-semibold hover:bg-gray-600 hover:text-white transition-colors"
                >
                  <span>Learn About Standard Screens</span>
                  <ArrowRight size={20} />
                </Link>

                <div className="mt-6 space-y-2">
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-gray-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Simple, reliable design</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-gray-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Easy manual cleaning</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-gray-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Cost-effective protection</span>
                  </div>
                </div>
              </div>

              {/* SCS2 Self-Cleaning Card */}
              <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                    <Droplets className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">SCS2 Self-Cleaning</h3>
                    <p className="text-sm text-gray-600">Automatic Intake Protection</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Self-cleaning suction screen uses pump discharge water to power internal spray nozzles. Only the internal spray bar rotates—the screen itself is stationary (no external moving parts). Automatically cleans while maintaining full flow—no external power or controls needed. Ideal for agricultural, municipal, and industrial intakes.
                </p>

                <Link
                  to="/filtration/suction-screens/self-cleaning"
                  className="flex items-center justify-between bg-white border-2 border-green-600 text-green-600 px-4 py-3 rounded-lg font-semibold hover:bg-green-600 hover:text-white transition-colors"
                >
                  <span>Learn About SCS2</span>
                  <ArrowRight size={20} />
                </Link>

                <div className="mt-6 space-y-2">
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Ideal for variable debris loads</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">No external power required</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Proven in manure ponds / dairy lagoons</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Continuous operation capability</span>
                  </div>
                </div>
              </div>

              {/* PSF Passive Fish Screen Card */}
              <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <Shield className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">PSF Passive Screen</h3>
                    <p className="text-sm text-gray-600">Fish-Friendly Intake</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Wedgewire passive screens maintain &lt;0.2 ft/sec approach velocity for fish-safe operation. Designed for hydropower intakes, dam penstock screening, and canal diversions. No moving parts—ideal for environmental compliance and low-maintenance applications.
                </p>

                <Link
                  to="/filtration/suction-screens/passive-fish-screens"
                  className="flex items-center justify-between bg-white border-2 border-blue-600 text-blue-600 px-4 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors"
                >
                  <span>Learn About PSF</span>
                  <ArrowRight size={20} />
                </Link>

                <div className="mt-6 space-y-2">
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-blue-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Regulatory fish passage compliance</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-blue-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">No moving parts or power</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-blue-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Wedgewire construction</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 max-w-3xl mx-auto bg-white border-2 border-blue-200 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2">Choosing Between Self-Cleaning and Passive</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                <span className="font-semibold">Self-Cleaning (SCS2)</span> works best with variable debris loads, when pump discharge water is available, and continuous operation is needed. <span className="font-semibold">Passive (PSF)</span> is required for fish-safe regulatory compliance, low-debris environments, or when simplicity and zero power consumption are priorities.
              </p>
            </div>
          </div>
        </section>

        {/* Water Box & Specialty Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <Link to="/filtration/water-intake">
              <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center hover:text-ctc-blue transition-colors cursor-pointer">Water Box & Specialty Filtration</h2>
            </Link>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Coarse intake screening and fine filtration solutions for pump stations, canal intakes, and specialty applications.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Water Box Card */}
              <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <Settings className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Water Box (WB)</h3>
                    <p className="text-sm text-gray-600">Coarse Intake Screening</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  General-purpose intake screening for ponds, canals, and sumps. Large screen area removes bulk debris (leaves, twigs) before water reaches pumps and filters.
                </p>

                <Link
                  to="/filtration/water-intake/water-box"
                  className="flex items-center justify-between bg-white border-2 border-blue-600 text-blue-600 px-4 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors mb-6"
                >
                  <span>Water Box Details</span>
                  <ArrowRight size={20} />
                </Link>

                <div className="space-y-2">
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-blue-600 mt-1 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-700">Large screen area for coarse solids</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-blue-600 mt-1 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-700">Float valve level control</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-blue-600 mt-1 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-700">Pump protection upstream</span>
                  </div>
                </div>
              </div>

              {/* Basket with Bag Card */}
              <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-300 rounded-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-orange-600 rounded-lg flex items-center justify-center mr-4">
                    <Filter className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Basket with Bag (BWB)</h3>
                    <p className="text-sm text-gray-600">Fine Filtration</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Fine filtration using replaceable bag inserts (1-200 micron) for algae, silt, and micro-particulates. Ideal for polishing and targeted particle removal.
                </p>

                <Link
                  to="/filtration/water-intake/basket-with-bag"
                  className="flex items-center justify-between bg-white border-2 border-orange-600 text-orange-600 px-4 py-3 rounded-lg font-semibold hover:bg-orange-600 hover:text-white transition-colors mb-6"
                >
                  <span>BWB Details</span>
                  <ArrowRight size={20} />
                </Link>

                <div className="space-y-2">
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-700">Replaceable filter bags (1-200 micron)</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-700">Fine algae and silt removal</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-700">Quick bag changeover</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mesh/Perf Quick Guide */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Mesh & Perforation Quick Guide</h2>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              Typical starting points for common applications. Every site varies based on water quality and system design.
            </p>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Application</th>
                    <th className="px-6 py-4 text-left font-semibold">Typical Starting Point</th>
                    <th className="px-6 py-4 text-left font-semibold">Micron Range</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-blue-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Drip Irrigation</td>
                    <td className="px-6 py-4 text-gray-700">~120 mesh</td>
                    <td className="px-6 py-4 text-gray-600">125 µm</td>
                  </tr>
                  <tr className="hover:bg-blue-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Micro-Sprinkler</td>
                    <td className="px-6 py-4 text-gray-700">~80 mesh</td>
                    <td className="px-6 py-4 text-gray-600">177 µm</td>
                  </tr>
                  <tr className="hover:bg-blue-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Center Pivot</td>
                    <td className="px-6 py-4 text-gray-700">~50 mesh</td>
                    <td className="px-6 py-4 text-gray-600">297 µm</td>
                  </tr>
                  <tr className="hover:bg-blue-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Intake Protection</td>
                    <td className="px-6 py-4 text-gray-700">~30 mesh / 1/32–1/16" perf</td>
                    <td className="px-6 py-4 text-gray-600">595 µm</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Important:</span> Final mesh or perforation selection depends on emitter orifice size, solids loading, maintenance interval requirements, and water source characteristics. Custom sizes beyond these standards are available—contact our filtration specialist team for application-specific recommendations.
              </p>
            </div>
          </div>
        </section>

        {/* Dealer Logos */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Trusted by Leading Dealers & Growers</h2>
            <DealerLogos />
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                    <ChevronDown
                      className={`flex-shrink-0 text-ctc-blue transition-transform ${
                        openFAQ === index ? 'transform rotate-180' : ''
                      }`}
                      size={24}
                    />
                  </button>
                  {openFAQ === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Select Your Filtration System?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Our filtration specialist team will help you choose the right filter, screen type, and cleaning technology for your application.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?topic=filtration"
                className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
              >
                <Mail className="mr-2" size={20} />
                Talk to a Filtration Specialist
              </Link>

              <Link
                to="/filtration/size-my-filter"
                className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg inline-flex items-center justify-center"
              >
                <Settings className="mr-2" size={20} />
                Size My Filter
              </Link>
            </div>

            <div className="mt-8 pt-8 border-t border-white/20">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-blue-100">
                <div className="flex items-center">
                  <Phone className="mr-2" size={20} />
                  <span>(509) 901-7476</span>
                </div>
                <div className="flex items-center">
                  <Mail className="mr-2" size={20} />
                  <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                    Andre@ctcwaterworks.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default FiltrationLandingPage;
